import { useState, useCallback, useRef } from "react";
import { Order } from "../../types/Sorters";
import { toast } from "react-toastify";
import { ArrowLeft } from "lucide-react";
import { playError } from "../helpers/audioPlayer";
import { useTranslation } from "../../i18n";
import HiddenScanInput from "../HiddenScanInput";
import ScanFlashOverlay from "../ScanFlashOverlay";
import "../../styles/scansheet.css";

const SorterTwoOrderDisplay = ({ availableOrders, selectedBay, selectOrder, closeBay }: { availableOrders: Order[], selectedBay: string, selectOrder: ({ order }: { order: Order }) => any, closeBay: () => void }) => {
    const { t } = useTranslation();
    const [inputText, set_inputText] = useState<string>("");
    const [scanStatus, set_scanStatus] = useState<"idle" | "success" | "error">("idle");
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);

    const flashScanStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        set_scanStatus(status);
        statusTimerRef.current = setTimeout(() => set_scanStatus("idle"), 500);
    };

    // Numeric-aware compare so double-digit positions order correctly (B2 before B18).
    const availableOrdersInBay = availableOrders.filter((order) => order.Bay === selectedBay).sort((order1, order2) => order1.Locations[0].localeCompare(order2.Locations[0], undefined, { numeric: true }));

    const handleOrderChoiceByScan = useCallback((scannedValue?: string) => {
        const val = scannedValue ?? inputText;
        set_inputText("");

        let foundOrder = false;

        availableOrdersInBay.forEach((order: Order) => {
            if (order.Locations.includes(val)) {
                selectOrder({ order });
                foundOrder = true;
                return;
            }
        });

        if (!foundOrder) {
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("no_order_in_location", { location: val }));
        }
    }, [inputText, availableOrdersInBay, selectOrder]);

    return (
        <div className="page-data" style={{ minHeight: "calc(100vh - 56px)" }}>
            <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("orders")}</h1>
                <button className="page-back-btn" onClick={() => closeBay()}><ArrowLeft size={20} /></button>
            </div>

            <div className="orders-container">
                <div className="order-container fill-colour"></div>

                {availableOrdersInBay.length === 0 && <h2>{t("no_orders")}</h2>}

                {availableOrdersInBay.map((order) => (
                    <div className={`order-container ${order.Priority && "priority-order"}`} key={order.OrderNumber} onClick={() => selectOrder({ order })}>
                        <div className="order-section">
                            <h3>{t("order_number")}</h3>
                            <p>{order.OrderNumber}</p>
                        </div>
                        <div className="order-section">
                            <h3>{t("locations")}</h3>
                            {order.Locations.map((location) => <p key={location}>{location}</p>)}
                        </div>
                        <div className="order-section">
                            <h3>{t("items")}</h3>
                            <p>{order.NumberOfItems.toString()}</p>
                        </div>
                    </div>
                ))}

                <div className="order-container fill-colour"></div>
            </div>

            <HiddenScanInput value={inputText} onChange={set_inputText} onSubmit={handleOrderChoiceByScan} disabled={false} disableCamera />
        </div>
    );
}

export default SorterTwoOrderDisplay;
